/**
 * The Returns tab: money sent back to its sender that has not yet been seen leaving the
 * bank, and the one action that closes each: naming the outgoing debit.
 *
 * Naming the debit is the only statement in the system that says a given debit is
 * explained, and the nightly cash proof believes it. So it is manager-only, takes a
 * step-up code, must be a different manager from the one who decided the return, and is
 * written once.
 */
import { useCallback, useEffect, useState } from 'react';
import StatusBadge from '../../components/StatusBadge';
import DebitPicker from '../../components/staff/DebitPicker';
import bankTransactionsService, { type OpenReturn } from '../../services/bankTransactions.service';
import type { BankDebitCandidate } from '../../services/payouts.service';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { cleanMfaInput } from '../../lib/mfaApproval';
import { formatDateTime } from '../../lib/datetime';
import { RETURN_REASONS } from './bankReturns.helpers';
import { colors } from '../../styles/theme';
import {
  SETTLEMENT_SOD_NOTE,
  isManagerRole,
  listView,
  oldestReturnFirst,
  settlementConfirmation,
  settlementProblem,
  submitSettlement,
  type ListView,
  type SettlementDraft,
} from './bankRecon.helpers';

const inputStyle = { background: colors.canvas, color: colors.ink, border: `1px solid ${colors.border}` };
const EMPTY: SettlementDraft = { debitId: '', mfaCode: '' };

export default function OpsReturnsTab(): JSX.Element {
  const role = authService.getRole();
  const [rows, setRows] = useState<OpenReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(oldestReturnFirst(await bankTransactionsService.listOpenReturns()));
    } catch (e: unknown) {
      setRows([]);
      setError(getApiErrorMessage(e, 'Could not load the open returns.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <ReturnsView
      view={listView({ loading, error, count: rows.length })}
      error={error}
      flash={flash}
      rows={rows}
      role={role}
      onSettled={(message) => { setFlash(message); void reload(); }}
    />
  );
}

export function ReturnsView({ view, error, flash, rows, role, onSettled }: Readonly<{
  view: ListView;
  error: string | null;
  flash: string | null;
  rows: OpenReturn[];
  role: string | null;
  onSettled: (message: string) => void;
}>): JSX.Element {
  return (
    <div>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Each row is money a manager decided to send back that we have not yet seen leave the
        bank. Until someone names the outgoing debit, the nightly cash proof keeps asking
        about it.
      </p>
      {flash && (
        <div className="rounded-lg p-3 mb-4 text-sm"
             style={{ background: colors.successBg, color: colors.success, border: `1px solid ${colors.successBorder}` }}>
          {flash}
        </div>
      )}
      {view === 'error' && error && (
        <div className="rounded-lg p-3 mb-4 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      {view === 'loading' && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
      {view === 'empty' && (
        <div className="rounded-2xl p-6 text-sm"
             style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
          Every return has been matched to the debit that carried it out.
        </div>
      )}
      {view === 'rows' && (
        <ul className="space-y-3">
          {rows.map((r) => <ReturnRow key={r.id} ret={r} role={role} onSettled={onSettled} />)}
        </ul>
      )}
    </div>
  );
}

function ReturnRow({ ret, role, onSettled }: Readonly<{
  ret: OpenReturn;
  role: string | null;
  onSettled: (message: string) => void;
}>): JSX.Element {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [draft, setDraft] = useState<SettlementDraft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The debits that can be this return leaving, listed when the form opens; picked, never typed.
  const [candidates, setCandidates] = useState<BankDebitCandidate[] | null>(null);
  const canAct = isManagerRole(role);

  useEffect(() => {
    if (!open || !canAct) return;
    let live = true;
    setCandidates(null);
    bankTransactionsService.returnDebitCandidates(ret.id)
      .then((list) => { if (live) setCandidates(list); })
      .catch((e: unknown) => {
        if (live) {
          setCandidates([]);
          setError(getApiErrorMessage(e, 'Could not list the bank debits.'));
        }
      });
    return () => { live = false; };
  }, [open, canAct, ret.id]);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const result = await submitSettlement(bankTransactionsService, ret.id, draft, role);
    setBusy(false);
    // The code is single-use; a refused attempt needs a fresh one either way.
    setDraft((d) => ({ ...d, mfaCode: '' }));
    if (result.ok) {
      setOpen(false);
      setConfirming(false);
      setDraft(EMPTY);
      onSettled(result.message);
    } else {
      setConfirming(false);
      setError(result.error);
    }
  };

  return (
    <li className="rounded-2xl p-4"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <ReturnSummary ret={ret} />
      <div className="mt-3">
        {canAct ? (
          <button type="button" onClick={() => { setOpen((v) => !v); setConfirming(false); }}
                  className="text-xs px-3 py-1.5 rounded-md font-semibold"
                  style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
            {open ? 'Cancel' : 'Confirm it left'}
          </button>
        ) : (
          <p className="text-xs" style={{ color: colors.mutedSoft }}>
            Only a manager can confirm that a return left the account.
          </p>
        )}
      </div>
      {open && canAct && (
        <SettlementForm
          idPrefix={ret.id}
          ret={ret}
          draft={draft}
          candidates={candidates}
          role={role}
          busy={busy}
          confirming={confirming}
          error={error}
          onChange={setDraft}
          onReview={() => setConfirming(true)}
          onBack={() => setConfirming(false)}
          onConfirm={() => { void submit(); }}
        />
      )}
    </li>
  );
}

export function ReturnSummary({ ret }: Readonly<{ ret: OpenReturn }>): JSX.Element {
  const reason = RETURN_REASONS.find((r) => r.value === ret.reason)?.label ?? ret.reason;
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="font-semibold" style={{ color: colors.ink }}>
          {String(ret.amount)} {ret.currency_code}
        </div>
        <div className="text-xs" style={{ color: colors.muted }}>
          Arrived {formatDateTime(ret.posted_at)} · return decided {formatDateTime(ret.decided_at)}
          {' '}· bank charge {String(ret.fee_amount)} {ret.fee_currency ?? ret.currency_code}
        </div>
        <div className="text-xs mt-0.5" style={{ color: colors.inkSoft }}>{reason}</div>
        <div className="text-xs mt-0.5" style={{ color: colors.inkSoft }}>
          Instruction to the bank: &ldquo;{ret.instruction}&rdquo;
        </div>
      </div>
      <StatusBadge tone="warning">
        {ret.notice_sent_at ? 'Sender told' : 'Not yet seen leaving'}
      </StatusBadge>
    </div>
  );
}

export function SettlementForm({
  idPrefix, ret, draft, candidates, role, busy, confirming, error, onChange, onReview, onBack,
  onConfirm,
}: Readonly<{
  idPrefix: string;
  ret: OpenReturn;
  draft: SettlementDraft;
  candidates: BankDebitCandidate[] | null;
  role: string | null;
  busy: boolean;
  confirming: boolean;
  error: string | null;
  onChange: (draft: SettlementDraft) => void;
  onReview: () => void;
  onBack: () => void;
  onConfirm: () => void;
}>): JSX.Element {
  const problem = settlementProblem(draft, role);
  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
      {error && (
        <div className="rounded-lg p-2 mb-3 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      <p className="text-xs mb-3" style={{ color: colors.warning }}>{SETTLEMENT_SOD_NOTE}</p>
      <div className="mb-3">
        <DebitPicker
          name={`${idPrefix}-debit`}
          legend="Bank debit that is this return leaving"
          emptyText="No imported debit fits the amount sent back yet. Import the statement and come back."
          allowNone={false}
          candidates={candidates}
          picked={draft.debitId}
          onPick={(id) => onChange({ ...draft, debitId: id })}
        />
        <p className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
          Only debits for the amount returned (the credit less the bank&apos;s charge), in the
          same currency, out of the account the money arrived in are offered.
        </p>
      </div>
      <label htmlFor={`${idPrefix}-mfa`} className="block text-xs font-semibold mb-1"
             style={{ color: colors.inkSoft }}>
        Authenticator code
      </label>
      <input id={`${idPrefix}-mfa`} value={draft.mfaCode} disabled={busy || confirming}
             inputMode="numeric" autoComplete="one-time-code"
             onChange={(e) => onChange({ ...draft, mfaCode: cleanMfaInput(e.target.value) })}
             className="w-40 px-3 py-2 rounded-lg text-sm mb-3" style={inputStyle} />

      {confirming ? (
        <div className="rounded-lg p-3" style={{ border: `1px solid ${colors.warning}` }}>
          <p className="text-sm mb-3" style={{ color: colors.ink }}>{settlementConfirmation(ret)}</p>
          <div className="flex gap-2">
            <button type="button" disabled={busy || problem !== null} onClick={onConfirm}
                    className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                    style={{ background: colors.navy, color: '#FFFFFF' }}>
              {busy ? 'Recording…' : 'Yes, record that it left'}
            </button>
            <button type="button" disabled={busy} onClick={onBack}
                    className="px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
              Go back
            </button>
          </div>
        </div>
      ) : (
        <>
          {problem && <p className="text-xs mb-2" style={{ color: colors.warning }}>{problem}</p>}
          <button type="button" disabled={problem !== null || busy} onClick={onReview}
                  className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                  style={{ background: colors.navy, color: '#FFFFFF' }}>
            Review before recording
          </button>
        </>
      )}
    </div>
  );
}
